'use client';

import { useEffect, useState } from 'react';
import type { ClubOperatingHourSlot } from '@court-sync/shared';
import { getDefaultClubOperatingHours } from '@court-sync/shared';
import { Switch } from '@/components/ui';
import {
  OperatingHoursScheduleEditor,
  dayScheduleRowsToSlots,
  toDayScheduleRows,
  type DayScheduleRow,
} from '@/components/club/OperatingHoursScheduleEditor';

interface LocationHoursOverrideSectionProps {
  value: ClubOperatingHourSlot[] | null | undefined;
  onChange: (value: ClubOperatingHourSlot[] | null) => void;
  disabled?: boolean;
}

export function LocationHoursOverrideSection({
  value,
  onChange,
  disabled = false,
}: LocationHoursOverrideSectionProps) {
  const [enabled, setEnabled] = useState(Boolean(value && value.length > 0));
  const [rows, setRows] = useState<DayScheduleRow[]>(() =>
    toDayScheduleRows(value && value.length > 0 ? value : getDefaultClubOperatingHours())
  );

  useEffect(() => {
    const hasOverride = Boolean(value && value.length > 0);
    /* eslint-disable react-hooks/set-state-in-effect -- sync local editor when parent value changes */
    setEnabled(hasOverride);
    setRows(toDayScheduleRows(hasOverride ? value! : getDefaultClubOperatingHours()));
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [value]);

  const handleEnabledChange = (checked: boolean) => {
    setEnabled(checked);
    if (!checked) {
      onChange(null);
      return;
    }
    onChange(dayScheduleRowsToSlots(rows));
  };

  const handleRowsChange = (nextRows: DayScheduleRow[]) => {
    setRows(nextRows);
    if (enabled) {
      onChange(dayScheduleRowsToSlots(nextRows));
    }
  };

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">Hours override</p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Optional. When enabled, courts at this location use these hours instead of the club
            default for court hire availability.
          </p>
        </div>
        <Switch
          checked={enabled}
          disabled={disabled}
          onCheckedChange={handleEnabledChange}
          aria-label="Enable location hours override"
        />
      </div>
      {enabled ? (
        <OperatingHoursScheduleEditor
          rows={rows}
          onChange={handleRowsChange}
          idPrefix="location-hours"
        />
      ) : null}
    </div>
  );
}
